import React, { useState } from 'react';
import { Check, Download, Upload, X } from 'lucide-react';
import {
  Field, OPTIONAL_COLUMNS, REQUIRED_COLUMNS, SUPPORTED_CURRENCIES, SUPPORTED_UNITS, Table,
  formatLabel, formatWhen, packPrice, pendingLabel, perUnit, priceChange, statusLabel,
} from './SupplierPriceListsShared';

// Price up is a cost increase (red); down is a saving (green).
const TONE = { up: { color: '#a33a2b', fontWeight: 600 }, down: { color: '#1f6b3f', fontWeight: 600 }, warn: { color: '#8a5a00' } };
const CADENCES =['weekly', 'daily', 'fortnightly', 'monthly', 'ad hoc'];

/** New source account. Deliberately no credential fields: the backend contract has none. */
export function SourceForm({ vendors, busy, onCreate }) {
  const [form, setForm] = useState({ supplier_id: '', supplier_account_reference: '', cadence: 'weekly', timezone: 'Europe/London' });
  const set = key => e => setForm({ ...form, [key]: e.target.value });
  const ready = form.supplier_id && form.supplier_account_reference.trim() && form.timezone.trim();
  return (
    <form className="mkt-form" aria-label="Add price-list source" onSubmit={async e => {
      e.preventDefault();
      if (!ready) return;
      const res = await onCreate({ ...form, supplier_account_reference: form.supplier_account_reference.trim(), format: 'csv' });
      if (res?.ok) setForm({ ...form, supplier_account_reference: '' });
    }}>
      <h2>Add a price-list source</h2>
      <p className="mkt-hint">No passwords or API keys are stored here. Access to a supplier's price file is agreed with the supplier; a new source starts as <strong>requested</strong> and can import once an administrator records the permission as approved.</p>
      <fieldset disabled={busy}>
        <div className="mkt-fields">
          <Field label="Supplier" value={form.supplier_id} onChange={set('supplier_id')} required
            options={[{ value: '', label: vendors.length ? 'Choose a vendor' : 'No vendors yet' }, ...vendors.map(v => ({ value: v.id, label: v.name || v.id }))]} />
          <Field label="Account reference" value={form.supplier_account_reference} onChange={set('supplier_account_reference')} required maxLength={120} placeholder="e.g. your customer number" />
          <Field label="Cadence" value={form.cadence} onChange={set('cadence')} options={CADENCES} />
          <Field label="Timezone" value={form.timezone} onChange={set('timezone')} required />
        </div>
        <button className="mkt-primary" type="submit" disabled={!ready}>Add source</button>
      </fieldset>
    </form>
  );
}

/** Owner/admin records the commercial basis for using a source's prices (or revokes it). */
export function SourcePermission({ source, busy, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [evidence, setEvidence] = useState('');
  const next = source.permission_state === 'approved' ? 'revoked' : 'approved';
  if (!open) {
    return <button type="button" disabled={busy} onClick={() => setOpen(true)}
      aria-label={`${next === 'approved' ? 'Approve' : 'Revoke'} source ${source.supplier_account_reference}`}>
      {next === 'approved' ? 'Approve…' : 'Revoke…'}</button>;
  }
  return (
    <form aria-label="Record source permission" onSubmit={async e => {
      e.preventDefault();
      const res = await onSubmit(source.source_id, { state: next, evidence: evidence.trim() });
      if (res?.ok) { setOpen(false); setEvidence(''); }
    }}>
      <input aria-label="Permission evidence" value={evidence} onChange={e => setEvidence(e.target.value)} minLength={10} maxLength={1000} required
        placeholder={next === 'approved' ? 'e.g. Supplier email 1 Oct 2026 permits use of our price file' : 'Why access was withdrawn'} />
      <button className="mkt-primary" type="submit" disabled={busy || evidence.trim().length < 10}>{next === 'approved' ? 'Approve' : 'Revoke'}</button>
      <button type="button" onClick={() => setOpen(false)}>Cancel</button>
    </form>
  );
}

/** CSV upload (FULL or DELTA) plus the optional Xero bills pull. */
export function ImportForm({ sources, vendorName, busy, onUpload, onXeroPull, blocked }) {
  const usable = sources.filter(s => s.can_publish);
  const [sourceId, setSourceId] = useState(usable[0]?.source_id || '');
  const [snapshot, setSnapshot] = useState('delta');
  const [file, setFile] = useState(null);
  return (
    <>
      <form className="mkt-form" aria-label="Upload price list" onSubmit={e => {
        e.preventDefault();
        if (!sourceId || !file) return;
        const body = new FormData();
        body.append('file', file);
        body.append('source_id', sourceId);
        body.append('snapshot_type', snapshot);
        onUpload(body);
      }}>
        <h2>Upload a price list</h2>
        {!usable.length && <p className="mkt-hint" role="note">No source at this site is approved to import yet. Add one under Sources; it can import once its permission is approved.</p>}
        <fieldset disabled={busy}>
          <div className="mkt-fields">
            <Field label="Source" value={sourceId} onChange={e => setSourceId(e.target.value)}
              options={[{ value: '', label: 'Choose a source' }, ...sources.map(s => ({
                value: s.source_id,
                label: `${vendorName(s.supplier_id)} · ${s.supplier_account_reference}${s.can_publish ? '' : ` (${statusLabel(s.permission_state)}, cannot import)`}`,
              }))]} />
            <Field label="Snapshot" value={snapshot} onChange={e => setSnapshot(e.target.value)}
              options={[{ value: 'delta', label: 'DELTA: only new or changed lines' }, { value: 'full', label: 'FULL: the complete list' }]} />
            <label className="mkt-field"><span>CSV file</span>
              <input type="file" aria-label="CSV file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <p className="mkt-hint">{snapshot === 'full'
            ? 'FULL: any line this supplier previously offered that is missing from the file is withdrawn when you approve.'
            : 'DELTA: lines missing from the file keep their current price.'}</p>
          <button className="mkt-primary" type="submit" disabled={!sourceId || !file || !sources.find(s => s.source_id === sourceId)?.can_publish}><Upload size={16} />Upload and preview</button>
        </fieldset>
        <details>
          <summary>Expected columns</summary>
          <p className="mkt-hint">Header row required. Required: <code>{REQUIRED_COLUMNS.join(', ')}</code>. Optional: <code>{OPTIONAL_COLUMNS.join(', ')}</code>.</p>
          <p className="mkt-hint">Units: {SUPPORTED_UNITS.join(', ')}. Currencies: {SUPPORTED_CURRENCIES.join(', ')} (default GBP). <code>tax_basis</code> (net or gross) is required whenever <code>pack_price</code> is given. Leave a price blank if unknown; zero is refused. UTF-8, up to 5 MB and 5,000 rows.</p>
        </details>
      </form>
      {blocked && <BlockedFile detail={blocked} />}
      {onXeroPull && (
        <section className="mkt-form" aria-label="Pull from Xero">
          <h2>Pull from Xero bills</h2>
          <p className="mkt-hint">Stages recent supplier bills from Xero as a batch to review, the same way as a CSV upload. Nothing is published until you approve it.</p>
          <button type="button" disabled={busy} onClick={onXeroPull}><Download size={16} />Pull from Xero</button>
        </section>
      )}
    </>
  );
}

function BlockedFile({ detail }) {
  const rows = Array.isArray(detail.row_errors) ? detail.row_errors : [];
  const files = Array.isArray(detail.file_errors) ? detail.file_errors : [];
  return (
    <section className="mkt-detail" aria-label="File errors">
      <h3>Nothing was staged: fix these and upload again</h3>
      {files.map(m => <p key={m} className="mkt-flag">{m}</p>)}
      {rows.length > 0 && <Table headers={['Line', 'Field', 'Problem']} label="Row errors">
        {rows.flatMap(r => (r.errors || []).map((er, i) => (
          <tr key={`${r.line}-${i}`}><td className="mkt-number">{r.line}</td><td>{er.field || '—'}</td><td>{er.message}</td></tr>
        )))}
      </Table>}
    </section>
  );
}

/** One batch, any source format: rows, change vs current, pending confirmations, decision. */
export function BatchReview({ detail, current, ingredients, canWrite, busy, vendorName, onApprove, onReject }) {
  const { batch, rows = [] } = detail;
  const pending = batch.pending_confirmations || {};
  const descriptions = batch.staged_descriptions || {};
  const [mappings, setMappings] = useState({});
  const [packs, setPacks] = useState({});
  const open = batch.status === 'open';
  const unresolved = Object.entries(pending).filter(([sku, reason]) => (
    reason === 'ambiguous_mapping' ? !mappings[sku] : reason === 'changed_pack' ? !packs[sku] : true
  ));
  const ingredientOptions = [{ value: '', label: 'Choose ingredient' }, ...ingredients.map(i => ({ value: i.id, label: `${i.name || i.id}${i.unit ? ` (${i.unit})` : ''}` }))];
  const approve = () => onApprove({
    reviewed_mappings: Object.fromEntries(Object.entries(mappings).filter(([sku, v]) => v && pending[sku] === 'ambiguous_mapping')),
    reviewed_pack_changes: Object.keys(packs).filter(sku => packs[sku] && pending[sku] === 'changed_pack'),
  });
  return (
    <section className="mkt-detail" aria-label="Batch review" data-testid="sp-batch-review" data-source-format={batch.source_format || ''}>
      <h2>{formatLabel(batch.source_format)} · {vendorName(batch.supplier_id)}</h2>
      <p>
        Status: <strong>{statusLabel(batch.status)}</strong> · {String(batch.snapshot_type || 'delta').toUpperCase()} · {batch.row_count ?? rows.length} rows · staged {formatWhen(batch.created_at)}
        {batch.published_at && <> · approved {formatWhen(batch.published_at)}{batch.published_by ? ` by ${batch.published_by}` : ''}</>}
      </p>
      <Table headers={['SKU', 'Description', 'Pack price', 'Per unit', 'Vs current', 'Tax', 'Valid to', 'Review']} empty={!rows.length} emptyText="No lines in this batch." label="Batch lines">
        {rows.map(r => {
          const change = open ? priceChange(r, current[r.supplier_sku]) : null;
          const reason = pending[r.supplier_sku];
          return (
            <tr key={r.revision_id || r.supplier_sku}>
              <td>{r.supplier_sku}</td>
              <td>{descriptions[r.supplier_sku] || '—'}</td>
              <td className="mkt-number">{r.withdrawn_at ? 'Withdrawn' : packPrice(r)}</td>
              <td className="mkt-number">{perUnit(r)}</td>
              <td style={TONE[change?.tone]}>{change ? change.label : '—'}</td>
              <td>{r.tax_basis || 'Unknown'}</td>
              <td>{r.valid_to || '—'}</td>
              <td>{reason && open ? (canWrite ? (
                reason === 'ambiguous_mapping'
                  ? <Field label={`Ingredient for ${r.supplier_sku}`} value={mappings[r.supplier_sku] || ''} options={ingredientOptions}
                      onChange={e => setMappings({ ...mappings, [r.supplier_sku]: e.target.value })} />
                  : <label className="mkt-check"><input type="checkbox" aria-label={`Confirm pack change for ${r.supplier_sku}`} checked={Boolean(packs[r.supplier_sku])}
                      onChange={e => setPacks({ ...packs, [r.supplier_sku]: e.target.checked })} />{pendingLabel(reason)}</label>
              ) : pendingLabel(reason)) : (r.ingredient_id ? 'Mapped' : '—')}</td>
            </tr>
          );
        })}
      </Table>
      {open && canWrite && (
        <div className="mkt-actions">
          <button className="mkt-primary" type="button" disabled={busy || unresolved.length > 0} onClick={approve}><Check size={16} />Approve and publish</button>
          <button type="button" disabled={busy} onClick={onReject}><X size={16} />Reject</button>
          {unresolved.length > 0 && <span className="mkt-hint">{unresolved.length} line(s) still need review before approval.</span>}
        </div>
      )}
      {open && !canWrite && <p className="mkt-hint">Waiting for a manager to approve or reject.</p>}
    </section>
  );
}
